import React, { useState } from "react";
import {
  Table,
  Tag,
  Button,
  Drawer,
  Descriptions,
  Divider,
  List,
  Space,
  Input,
  Typography,
  Popconfirm,
  Spin,
  Empty,
  Timeline,
} from "antd";
import {
  PaperClipOutlined,
  DownloadOutlined,
  CheckOutlined,
  CloseOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";

const { Text, Title } = Typography;

const STAGE_LABELS = {
  HOD: "Head of Department",
  SECTION_HEAD: "Section Head",
  DEPUTY_HEAD: "Deputy Headteacher",
  TO_BE_UNLOCKED: "Deputy Headteacher",
};

const TYPE_LABELS = {
  scheme: "Scheme of Work",
  lesson_note: "Lesson Note",
  lesson_plan: "Lesson Plan",
  teaching_aid: "Teaching Aid",
  practical_guide: "Practical Guide",
};

const getDisplayDescription = (description) => {
  if (typeof description === "string") {
    return description.trim() || "-";
  }

  return description || "-";
};

const ApprovalQueueTable = ({
  queue,
  loading,
  role,
  onApprove,
  onReject,
  onUnlock,
}) => {
  const [selected, setSelected] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [remarks, setRemarks] = useState("");
  const [acting, setActing] = useState(false);

  const canUnlock = (record) =>
    role === "deputy_head" &&
    (record?.currentStage === "TO_BE_UNLOCKED" ||
      record?.status === "Approved");

  const openDrawer = (record) => {
    setSelected(record);
    setRemarks("");
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setSelected(null);
    setRemarks("");
  };

  const handleApproveOrUnlock = async () => {
    if (!selected) return;
    setActing(true);
    const ok = canUnlock(selected)
      ? await onUnlock(selected.id)
      : await onApprove(selected.id, remarks);
    setActing(false);
    if (ok) closeDrawer();
  };

  const handleReject = async () => {
    if (!selected || !remarks.trim()) return;
    setActing(true);
    const ok = await onReject(selected.id, remarks);
    setActing(false);
    if (ok) closeDrawer();
  };

  const columns = [
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      sorter: (a, b) => (a.title || "").localeCompare(b.title || ""),
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      render: (type) => <Tag color="purple">{TYPE_LABELS[type] || type}</Tag>,
    },
    {
      title: "Teacher",
      dataIndex: "teacherName",
      key: "teacherName",
      render: (v) => v || "-",
    },
    {
      title: "Academic Year",
      dataIndex: "academicYear",
      key: "academicYear",
    },
    {
      title: "Term",
      dataIndex: "term",
      key: "term",
      render: (v) => v || "-",
    },
    {
      title: "Stage",
      dataIndex: "currentStage",
      key: "currentStage",
      render: (stage) => (
        <Tag color="orange">{STAGE_LABELS[stage] || stage || "-"}</Tag>
      ),
    },
    {
      title: "Submitted On",
      dataIndex: "submittedAt",
      key: "submittedAt",
      render: (date) => (date ? dayjs(date).format("DD MMM YYYY") : "-"),
    },
    {
      title: "Action",
      key: "action",
      align: "center",
      render: (_, record) => (
        <Button type="primary" size="small" onClick={() => openDrawer(record)}>
          {canUnlock(record) ? "Unlock" : "Review"}
        </Button>
      ),
    },
  ];

  return (
    <>
      <Table
        rowKey="id"
        columns={columns}
        dataSource={queue}
        loading={loading}
        pagination={{
          pageSize: 20,
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "50"],
        }}
        scroll={{ x: 900 }}
        size="small"
      />

      <Drawer
        title={
          <Title level={5} style={{ margin: 0 }}>
            Review: {selected?.title}
          </Title>
        }
        open={drawerOpen}
        onClose={closeDrawer}
        width={620}
        destroyOnClose
      >
        {!selected ? (
          <Spin />
        ) : (
          <>
            <Descriptions column={1} bordered size="small">
              <Descriptions.Item label="Title">
                {selected.title}
              </Descriptions.Item>
              <Descriptions.Item label="Type">
                <Tag color="purple">
                  {TYPE_LABELS[selected.type] || selected.type}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Teacher">
                {selected.teacherName || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Academic Year">
                {selected.academicYear || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Term">
                {selected.term || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Stage">
                <Tag color="orange">
                  {STAGE_LABELS[selected.currentStage] || selected.currentStage}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Description">
                {getDisplayDescription(selected.description)}
              </Descriptions.Item>
            </Descriptions>

            {selected.files?.length > 0 && (
              <>
                <Divider orientation="left">Files</Divider>
                <List
                  size="small"
                  dataSource={selected.files}
                  renderItem={(file) => (
                    <List.Item
                      actions={[
                        <Button
                          type="text"
                          size="small"
                          icon={<DownloadOutlined />}
                          href={file.fileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        />,
                      ]}
                    >
                      <Space>
                        <PaperClipOutlined />
                        <Text>{file.filename}</Text>
                      </Space>
                    </List.Item>
                  )}
                />
              </>
            )}

            {selected.files?.length === 0 && (
              <Empty
                description="No files attached"
                style={{ margin: "16px 0" }}
              />
            )}

            {selected.approvals?.length > 0 && (
              <>
                <Divider orientation="left">Approval History</Divider>
                <Timeline
                  items={selected.approvals.map((a) => ({
                    color: a.action === "Approved" ? "green" : "red",
                    dot:
                      a.action === "Approved" ? (
                        <CheckCircleOutlined style={{ color: "#52c41a" }} />
                      ) : (
                        <CloseCircleOutlined style={{ color: "#ff4d4f" }} />
                      ),
                    children: (
                      <div>
                        <Text strong>{a.stage}</Text>
                        <Tag
                          color={a.action === "Approved" ? "green" : "red"}
                          style={{ marginLeft: 8 }}
                        >
                          {a.action}
                        </Tag>
                        <div>
                          <Text type="secondary">{a.approverName}</Text>
                        </div>
                        {a.remarks && (
                          <div>
                            <Text italic>{a.remarks}</Text>
                          </div>
                        )}
                        <div>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {a.approvedAt
                              ? dayjs(a.approvedAt).format("DD MMM YYYY HH:mm")
                              : ""}
                          </Text>
                        </div>
                      </div>
                    ),
                  }))}
                />
              </>
            )}

            <Divider orientation="left">Decision</Divider>
            {!(
              canUnlock(selected) &&
              (selected?.isUnlocked || selected?.status === "Unlocked")
            ) && (
              <div style={{ marginBottom: 12 }}>
                <label
                  style={{ fontWeight: 600, display: "block", marginBottom: 6 }}
                >
                  Remarks{" "}
                  {!canUnlock(selected) && (
                    <Text type="secondary">(required for rejection)</Text>
                  )}
                </label>
                <Input.TextArea
                  rows={3}
                  placeholder="Enter remarks..."
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                />
              </div>
            )}

            <Space>
              {canUnlock(selected) &&
              (selected?.isUnlocked || selected?.status === "Unlocked") ? (
                <Tag color="blue" style={{ padding: "4px 12px" }}>
                  Already Unlocked
                </Tag>
              ) : (
                <Popconfirm
                  title={
                    canUnlock(selected)
                      ? "Unlock this material?"
                      : "Approve this material?"
                  }
                  description={
                    canUnlock(selected)
                      ? "This will make the material available to students."
                      : "It will move to the next approval stage."
                  }
                  onConfirm={handleApproveOrUnlock}
                  okText="Confirm"
                  cancelText="Cancel"
                >
                  <Button
                    type="primary"
                    icon={<CheckOutlined />}
                    loading={acting}
                  >
                    {canUnlock(selected) ? "Unlock" : "Approve"}
                  </Button>
                </Popconfirm>
              )}

              {!canUnlock(selected) && (
                <Popconfirm
                  title="Reject this material?"
                  description="The material will return to the teacher for correction and resubmission. Remarks are required."
                  onConfirm={handleReject}
                  okText="Reject"
                  okButtonProps={{ danger: true }}
                  cancelText="Cancel"
                  disabled={!remarks.trim()}
                >
                  <Button
                    danger
                    icon={<CloseOutlined />}
                    loading={acting}
                    disabled={!remarks.trim()}
                  >
                    Reject
                  </Button>
                </Popconfirm>
              )}
            </Space>
          </>
        )}
      </Drawer>
    </>
  );
};

export default ApprovalQueueTable;
